import { Button } from '@lt/components';
import styled from 'styled-components';
import type { FC } from 'react';
import React from 'react';
import { i18n, useTranslation } from '@lt/localization';
import Icon from 'src/components/Icon';
import { LT_PHONE_NUMBER } from 'src/constants/common';
import { pluralizeOptions } from 'src/utils/pluralizeUtils';
import { pluralize } from 'src/utils/stringUtils';
import { scrollToFlights } from 'checkout/utils/globalUtils';
import type { PropsFromRedux } from '.';
import PackageInfoItem, { StyledContent } from '../components/PackageInfoItem';

const FLIGHT_ICON = <Icon type="plane" />;
const FETCHING_TEXT = i18n.t('checkout.packageInfo.flight.update');
const makeFlightOptionsLabel = (flightsOptionsCount) =>
  `${i18n.t('checkout.packageInfo.flight.ticket')}. ${pluralize(
    flightsOptionsCount,
    [
      i18n.t('checkout.packageInfo.flight.access', { count: 1 }),
      i18n.t('checkout.packageInfo.flight.access', { count: 2 }),
      i18n.t('checkout.packageInfo.flight.access', { count: 2 }),
    ],
  )}
  ${flightsOptionsCount} ${pluralizeOptions(flightsOptionsCount)}`;

const PackageFlightInfo: FC<PropsFromRedux & { isArchived: boolean }> = ({
  flightsOptionsCount = 0,
  fetching = false,
  isManualPackage = false,
  isMobile = false,
  isArchived,
}) => {
  const { t } = useTranslation();
  const hasError = (!flightsOptionsCount && !fetching) || isManualPackage;
  const hasFlights = flightsOptionsCount > 0;
  let text = '';
  if (!isManualPackage) {
    text = hasFlights
      ? makeFlightOptionsLabel(flightsOptionsCount)
      : `${t('checkout.packageInfo.flight.noInfo')} ${LT_PHONE_NUMBER}`;
  }
  const showGoToFlightsBtn = !fetching && hasFlights && !isMobile;
  return !isArchived || (isArchived && hasFlights) ? (
    <StyledPackageInfoItem
      icon={FLIGHT_ICON}
      text={fetching ? FETCHING_TEXT : text}
      isMobile={isMobile}
      hasError={hasError}
      $hasFlight={hasFlights}
    >
      {isManualPackage ? (
        <div className="flex flex-column">
          <h4 className="like-h4">
            {t('checkout.packageInfo.flight.noInclude')}
          </h4>
          <StyledText>{t('checkout.packageInfo.flight.selectNo')}</StyledText>
        </div>
      ) : (
        showGoToFlightsBtn && (
          <StyledButton
            type="button"
            onClick={scrollToFlights}
            size="tiny"
            isMobile={isMobile}
          >
            {t('checkout.packageInfo.flight.select')}
          </StyledButton>
        )
      )}
    </StyledPackageInfoItem>
  ) : null;
};

const StyledPackageInfoItem = styled(PackageInfoItem)<{
  $hasFlight: boolean;
}>`
  margin-bottom: 6px;
  align-items: center;
  ${({ isMobile }) =>
    isMobile &&
    `
    left: -5px;
    padding: 5px;
  `}
  ${({ $hasFlight }) =>
    $hasFlight &&
    `
    ${StyledContent} {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  `}
`;

const StyledButton = styled(Button)<{ isMobile: boolean }>`
  border-radius: 2px;
  padding: 6px 8px;
  height: auto;
  font-size: 14px;
  line-height: 16px;
  white-space: nowrap;
  ${({ isMobile }) =>
    isMobile &&
    `
    margin-top: 5px;
    margin-left: 35px;
    font-size: 13px;
  `}
`;

const StyledText = styled.span`
  font-size: 12px;
  color: #4d4d4d;
  & + * {
    margin-left: 13px;
  }
`;

export default PackageFlightInfo;
